import { ARRANGEMENT_FEE, MONTHS_IN_YEAR } from '../constants/common';

type TotalBorrowingCostParams = {
  loanAmount: number;
  annualInterestRate: number;
  monthlyAmortization: number;
  numberOfMonths: number;
  fees?: number;
};

/**
 * Calculates the total amount to pay over the life of the loan:
 *
 *   total = loan principal + total interest paid + fees
 *
 * The total interest is derived from an amortization schedule where interest is
 * charged on the remaining balance each month. Interest accrues until the earlier
 * of full repayment or `numberOfMonths`; if the balance is not fully amortized by
 * then, the remaining principal is assumed to be repaid at term end.
 */
export function getTotalBorrowingCost({
  loanAmount,
  annualInterestRate,
  monthlyAmortization,
  numberOfMonths,
  fees = ARRANGEMENT_FEE,
}: TotalBorrowingCostParams): number {
  if (
    loanAmount <= 0 ||
    numberOfMonths <= 0 ||
    monthlyAmortization < 0 ||
    annualInterestRate < 0 ||
    fees < 0
  ) {
    return 0;
  }

  const monthlyRate = annualInterestRate / 100 / MONTHS_IN_YEAR;
  let remainingBalance = loanAmount;
  let totalInterest = 0;

  for (let month = 0; month < numberOfMonths && remainingBalance > 0; month++) {
    totalInterest += remainingBalance * monthlyRate;
    remainingBalance -= monthlyAmortization;
  }

  return loanAmount + totalInterest + fees;
}
